// @ts-strict-ignore
// resources/js/components/smart_bid_settings/SmartBidSettingsForm.tsx
//
// The Smart Bidding (automation) settings form. Bid limits (min/max bid) are
// no longer edited here — they are a standalone concept managed by the Bid
// Limits column/popover. See app/Services/EntitySmartBidSettingWriter.php.
import React, { useEffect, useState } from 'react'
import classNames, { ClassValue } from 'classnames'
import { FormInput } from '../auth/common'
import { Callout } from '../ui/callout'

interface SmartBidSettingFormProps {
    className: ClassValue
    defaultSbs: Models.SmartBidSetting
    loading: boolean
    onChange: (any) => any
}

/**
 * A stored integer value (or null) -> input string for display.
 * null/undefined render as an empty field so missing values stay
 * distinguishable from real zeroes.
 */
const toInput = (value: number | string | null | undefined, divisor: number = 1): string => {
    if (value === null || value === undefined) return ''
    const num = typeof value === 'number' ? value : parseFloat(value)
    if (isNaN(num)) return ''
    return (num / divisor).toString()
}

/**
 * An input string -> number, or null when the field is left blank.
 * Blank MUST map to null (not 0), so omitted numeric settings do not turn into
 * real zero values.
 */
const toNumberOrNull = (value: string): number | null => {
    const trimmed = (value ?? '').trim()
    if (trimmed === '' || trimmed === '-' || trimmed === '.' || trimmed === '-.') {
        return null
    }
    const parsed = parseFloat(trimmed)
    return isNaN(parsed) ? null : parsed
}

/**
 * Same as toNumberOrNull but scales the value (e.g. dollars -> cents) and
 * rounds to an integer — the backing columns are integer cents, and
 * `parseFloat('0.02') * 100` is otherwise 2.0000000000000004.
 */
const toScaledOrNull = (value: string, factor: number): number | null => {
    const parsed = toNumberOrNull(value)
    return parsed === null ? null : Math.round(parsed * factor)
}

export const SmartBidSettingForm = ({
    className,
    defaultSbs,
    loading,
    onChange,
}: SmartBidSettingFormProps) => {
    const [name, setName] = useState(defaultSbs.name)
    const [targetAcosThreshold, setTargetAcosThreshold] = useState(
        toInput(defaultSbs.target_acos_threshold, 100),
    )
    const [maximumBidAdjustment, setMaximumBidAdjustment] = useState(
        toInput(defaultSbs.maximum_bid_adjustment, 100),
    )
    const [lookbackDays, setLookbackDays] = useState(toInput(defaultSbs.lookback_days))
    const editing = !!defaultSbs.id

    useEffect(() => {
        onChange({
            name,
            target_acos_threshold: toScaledOrNull(targetAcosThreshold, 100),
            maximum_bid_adjustment: toScaledOrNull(maximumBidAdjustment, 100),
            lookback_days: toNumberOrNull(lookbackDays),
        })
    }, [name, targetAcosThreshold, maximumBidAdjustment, lookbackDays])

    return (
        <div className={classNames(className)}>
            {editing && (
                <Callout intent="warning" className="mb-3">
                    This will edit the Smart Bidding settings for this item and any items that
                    inherit them.
                </Callout>
            )}
            <FormInput
                type="text"
                label="Name"
                disabled={loading}
                value={name}
                onChange={setName}
            />
            <FormInput
                type="percentage"
                label="Target ACoS Threshold"
                disabled={loading}
                placeholder={defaultSbs.target_acos_threshold?.toString() ?? ''}
                value={targetAcosThreshold}
                onChange={(v) => setTargetAcosThreshold(v)}
            />
            <FormInput
                type="percentage"
                label="Maximum Bid Adjustment"
                disabled={loading}
                placeholder={defaultSbs.maximum_bid_adjustment?.toString() ?? ''}
                value={maximumBidAdjustment}
                onChange={(v) => setMaximumBidAdjustment(v)}
            />
            <FormInput
                type="number"
                label="Lookback Days"
                disabled={loading}
                placeholder={defaultSbs.lookback_days?.toString() ?? ''}
                value={lookbackDays}
                onChange={(v) => setLookbackDays(v)}
            />
        </div>
    )
}
